import { DEFAULT_CLAIM_CLIENT_UPDATE_CONFIGURATION } from '@shared/constants';

export const CLAIM_MILESTONES = {
    received: {
        internalName: 'received',
        label: 'Received',
    },
    lodged: {
        internalName: 'lodged',
        label: 'Lodged',
    },
    date_of_loss: {
        internalName: 'date_of_loss',
        label: 'Date of loss',
    },
    status_changed_to: {
        internalName: 'status_changed_to_',
        label: 'Status changed to ',
    },
    decision_changed_to: {
        internalName: 'decision_changed_to_',
        label: 'Decision changed to ',
    },
    triage_changed_to: {
        internalName: 'triage_changed_to_',
        label: 'Triage outcome changed to ',
    },
    email_received: {
        internalName: 'email_received_from_',
        label: 'E-mail received from',
    },
    reserve_added: {
        internalName: 'reserve_added',
        label: 'Reserve added',
    },
    reserve_changed: {
        internalName: 'reserve_changed',
        label: 'Reserve changed',
    },
    reserve_payment_made: {
        internalName: 'reserve_payment_made',
        label: 'Payment made',
    },
    invoice_added: {
        internalName: 'invoice_added',
        label: 'Invoice added',
    },
    invoice_sent: {
        internalName: 'invoice_sent',
        label: 'Invoice sent',
    },
    recovery_payment_received: {
        internalName: 'recovery_payment_received',
        label: 'Recovery payment received',
    },
    excess_payment_received: {
        internalName: 'excess_payment_received',
        label: 'Excess payment received',
    },
    owner_changed: {
        internalName: 'owner_changed',
        label: 'Claim owner changed',
    },
    ph_payment_details_changed: {
        internalName: 'ph_payment_details_changed',
        label: 'Policy holder payment details changed',
    },
    handling_party_changed: {
        internalName: 'handling_party_changed',
        label: 'Handling party changed',
    },
    incoming_call: {
        internalName: 'incoming_call_from_',
        label: 'Incoming call from ',
    },
    outgoing_call: {
        internalName: 'outgoing_call_to_',
        label: 'Outgoing call to ',
    },
    supplier_job_added: {
        internalName: 'supplier_job_added',
        label: 'Supplier job added',
    },
    supplier_job_removed: {
        internalName: 'supplier_job_removed',
        label: 'Supplier job removed',
    },
    updateClaimClientOnProgress: {
        internalName: 'update_claim_client_on_progress',
        label: DEFAULT_CLAIM_CLIENT_UPDATE_CONFIGURATION.milestone.labelWhenCompleted,
    },
};

/** Who is lodging the claim — captured by the Espirito FNOL lookup demo (CUR-217). */
export const CLAIMANT_ROLES = ['driver', 'fleet_manager', 'depot_manager'] as const;
export type ClaimantRole = (typeof CLAIMANT_ROLES)[number];

/**
 * Incident geo-location captured during FNOL lodgement (CUR-237). Signed decimal
 * degrees (negative latitude = southern hemisphere) — the shape the future
 * closest-repairer lookup will query against.
 */
export type ClaimIncidentLocation = {
    latitude: number;
    longitude: number;
    /** Metres, from the Geolocation API. */
    accuracy?: number;
    source: 'device' | 'manual';
    /** ISO 8601, when the device position was captured. */
    capturedAt?: string;
};

/**
 * Display-only copy of the fleet vehicle matched by the lodgement lookup
 * (CUR-494). The authoritative claim↔vehicle relationship is
 * `Claim.fleetVehicleId`, set server-side from the signed vehicle proof.
 */
export type ClaimFleetVehicleSnapshot = {
    registration: string;
    description?: string | null;
    operator?: string | null;
};

/** Validated at the public submission boundary by `claimJsonDataSchema` in `@shared/schemas`. */
export type ClaimJsonData = {
    /** Absent until the lodgement Conditions step is confirmed. */
    claimNotResultOfIllegalAct?: boolean;
    questionnaireDeclarationTitle?: string | null;
    questionnaireDeclarationBody?: string | null;
    claimantRole?: ClaimantRole;
    incidentLocation?: ClaimIncidentLocation;
    fleetVehicleSnapshot?: ClaimFleetVehicleSnapshot;
};

export type ThirdPartyContactDef = {
    id: string;
    name: string;
    allowAsMainContact: boolean;
};

export type ThirdPartyContactConfig = ThirdPartyContactDef[];

export * from './contact';
